"use client";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Table from "../../../../components/Table";
import Button from "../../../../components/Button";
import styles from "../../MedicalHistory/css/clinicHistory.module.css";
import { fetchClinicScriptDetails as fetchClinicScriptDetailsApi } from "../../../../api/dispensary/script/clinicScriptApi.js";

export default function ClinicScriptDetails() {
  const { scriptNo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [scriptData, setScriptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const formatDateTimeZA = (val) => {
    if (!val) return "";
    if (typeof val === 'string') {
      const s = val.trim();
      if (!s) return "";
      // If it's already a simple date (YYYY-MM-DD), keep as-is
      const m = s.match(/^\d{4}-\d{2}-\d{2}$/);
      if (m) return m[0];
    }

    try {
      const d = val instanceof Date ? val : new Date(val);
      if (Number.isNaN(d.getTime())) return "";

      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Africa/Johannesburg',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).formatToParts(d);

      const get = (type) => parts.find(p => p.type === type)?.value || '';
      const yyyy = get('year');
      const mm = get('month');
      const dd = get('day');
      const hh = get('hour');
      const mi = get('minute');
      const ss = get('second');
      const datePart = (yyyy && mm && dd) ? `${yyyy}-${mm}-${dd}` : '';
      const timePart = (hh && mi && ss) ? `${hh}:${mi}:${ss}` : '';
      return [datePart, timePart].filter(Boolean).join(' ');
    } catch {
      return "";
    }
  };
  
  // Get patient information from navigation state
  const patientInfo = location.state || {};

  console.log('[DEBUG] Component rendered, scriptNo from URL:', scriptNo);
  console.log('[DEBUG] Patient info from navigation:', patientInfo);

  useEffect(() => {
    console.log('[DEBUG] useEffect triggered, scriptNo:', scriptNo);
    
    const fetchClinicScriptDetails = async () => {
      if (!scriptNo) {
        console.log('[DEBUG] No scriptNo provided');
        return;
      }
      
      console.log('[DEBUG] Fetching clinic script details for:', scriptNo);
      
      try {
        setLoading(true);
        const data = await fetchClinicScriptDetailsApi(scriptNo);
        
        console.log('[DEBUG] API response:', data);
        console.log('[DEBUG] Drugs data:', data?.drugs);
        
        if (!data) {
          console.log('[DEBUG] No data returned from API');
          setError("Script not found");
          return;
        }

        // The API now returns data in the same format as the original process script API
        const pharmacist = data.pharmacist
          ? `${data.pharmacist.firstname || ''} ${data.pharmacist.surname || ''}`.trim()
          : '';
        const practitioner = data.practitioner
          ? `${data.practitioner.title ? data.practitioner.title + ' ' : ''}${data.practitioner.firstname || ''} ${data.practitioner.surname || ''}`.trim()
          : '';

        // Process drugs to ensure all fields are properly populated
        const processedDrugs = Array.isArray(data.drugs) ? data.drugs.map(drug => ({
          drugName: drug.drugName || drug.drugname || '',
          drugname: drug.drugName || drug.drugname || '', // For backward compatibility
          quantity: drug.quantity || 0,
          dosage: drug.dosage || '',
          frequency: drug.frequency || '',
          duration: drug.duration || '',
          instructions: drug.instructions || '',
          packSize: drug.packSize || '',
          strength: drug.strength || '',
          icd10: drug.icd10 || '',
          nappiCode: drug.nappiCode || '',
          costPrice: drug.costPrice || '',
          // Add any other fields that might be needed
          prescriptionDrugId: drug.prescriptionDrugId || null,
          sku: drug.sku || null,
        })) : [];

        console.log('[DEBUG] Processed drugs:', processedDrugs);

        const clinicRef = data.script?.clinicreference || data.script?.clinicReference || '';
        const repeatCount = Number(data.script?.repeatCount ?? data.script?.repeatcount ?? 0) || 0;
        const isChronic = !!(data.script?.isChronic ?? data.script?.ischronic);

        setScriptData({
          scriptNumber: clinicRef || data.script?.id || scriptNo,
          date: data.script?.date || '',
          paymentType: data.script?.paymentType || '',
          practitioner,
          pharmacist,
          patient: data.patient || null,
          repeatCount,
          isChronic,
          drugs: processedDrugs,
          // Add beneficiary information from navigation state
          isBeneficiary: patientInfo.isBeneficiary || false,
          patientId: patientInfo.patientId || data.patient?.patientId || null,
          profileNo: patientInfo.profileNo || data.patient?.profileNo || data.patient?.profilenumber || null,
        });
      } catch (err) {
        console.error('Failed to fetch clinic script details:', err);
        console.error('Error response:', err.response);
        console.error('Error message:', err.message);
        setError(`Failed to load script details: ${err.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchClinicScriptDetails();
  }, [scriptNo]);

  const scriptInfo = [
    { label: 'Clinic Reference', value: scriptData?.scriptNumber || '' },
    { label: 'Date', value: formatDateTimeZA(scriptData?.date) },
    { label: 'Payment Type', value: scriptData?.paymentType || '' },
    { label: 'Patient Type', value: scriptData?.isBeneficiary ? 'Beneficiary' : 'Main Member' },
    { label: 'Practitioner', value: scriptData?.practitioner || '' },
    { label: 'Pharmacist', value: scriptData?.pharmacist || '' },
  ];

  const scriptColumns = [
    { header: 'Description', accessor: 'label' },
    { header: 'Value', accessor: 'value' },
  ];

  const medicationColumns = [
    { header: 'Drug Name', accessor: 'drugname' },
    { header: 'Quantity', accessor: 'quantity' },
    { header: 'Instructions', accessor: 'instructions' },
    { header: 'Pack Size', accessor: 'packSize' },
    { header: 'Strength', accessor: 'strength' },
    { header: 'ICD-10', accessor: 'icd10' },
    { header: 'Nappi Code', accessor: 'nappiCode' },
    { header: 'Cost Price', accessor: 'costPrice' },
  ];

  const handleProcessScript = () => {
    // Convert clinic script drugs to medication card format
    const medications = scriptData?.drugs?.map((drug, index) => {
      // Convert ICD-10 string to array if it exists
      let icd10Array = [];
      if (drug.icd10 && drug.icd10.trim()) {
        // Split by comma and trim each code
        icd10Array = drug.icd10.split(',').map(code => code.trim()).filter(code => code);
      }
      
      return {
        id: index + 1,
        drugName: drug.drugname || drug.drugName || '', // Handle both formats
        quantity: drug.quantity || 1,
        packSize: drug.packSize || '',
        strength: drug.strength || '',
        icd10: icd10Array.length > 0 ? icd10Array[0] : '', // First ICD-10 code as string
        icd10Options: icd10Array, // Full array for dropdown options
        instructions: drug.instructions || '',
        instructionsOther: '',
        sku: drug.sku || null,
        nappiCode: drug.nappiCode || '',
        costPrice: drug.costPrice || '',
        stockText: '',
        stockQty: undefined,
        toFollowStruck: false,
        deliveryOption: '',
        saved: false,
      };
    }) || [];

    const repeatCount = Number(scriptData?.repeatCount) || 0;
    const initialIsRepeat = repeatCount > 0;
    const initialRepeatCount = repeatCount;
    const initialIsChronic = !!scriptData?.isChronic;

    // Navigate to new script form with prepopulated data
    navigate('/customer/dispensary/new-script', {
      state: {
        scriptNo: scriptData?.scriptNumber,
        prefillMedications: true,
        medications: medications,
        initialIsRepeat,
        initialRepeatCount,
        initialIsChronic,
        // Add patient information for prepopulation
        patientId: scriptData?.patient?.patientId || '',
        profileNo: scriptData?.profileNo || scriptData?.patient?.profileNo || scriptData?.patient?.profilenumber || '',
        // Add patient name as initialPatient for direct prepopulation
        initialPatient: `${scriptData?.patient?.title || ''} ${scriptData?.patient?.firstname || ''} ${scriptData?.patient?.surname || ''}`.trim(),
        patientName: `${scriptData?.patient?.surname || ''} ${scriptData?.patient?.firstname ? scriptData.patient.firstname.charAt(0)+'.' : ''} (${scriptData?.patient?.title || 'Mr/Ms'})`.trim(),
        initialPractitioner: scriptData?.practitioner || '',
        practitionerDetails: scriptData?.practitionerObj || null,
        // Add any other relevant clinic script data
        clinicScriptData: {
          date: scriptData?.date,
          paymentType: scriptData?.paymentType,
          practitioner: scriptData?.practitioner,
        }
      }
    });
  };

  const handleViewInvoice = () => {
    navigate(`/customer/dispensary/clinic-script/invoice/${scriptNo}`, {
      state: patientInfo,
    });
  };

  if (loading) {
    return (
      <div className={styles.app}>
        <Navbar />
        <main className={styles.content}>
          <div className={styles.pageHeader}>
            <h2>Loading clinic script details...</h2>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.app}>
        <Navbar />
        <main className={styles.content}>
          <div className={styles.pageHeader}>
            <h2 className={styles.pageTitle}>Clinic Script</h2>
          </div>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p style={{ color: 'red', marginBottom: '20px' }}>{error}</p>
            <button onClick={() => navigate(-1)} style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}>
              Go Back
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar />

        <main className={styles.content}>
          <div className={styles.pageHeader}>
            <h2 className={styles.pageTitle}>Clinic Script</h2>
          </div>

          <div className={styles.tablesContainer}>
            <div className={styles.scriptTableSection}>
              <h3 className={styles.sectionTitle}>Script Information</h3>
              <Table columns={scriptColumns} data={scriptInfo} />
            </div>

            <div className={styles.medicationTableSection}>
               <h3 className={styles.sectionTitle2}>Medication Prescribed</h3>
              <Table columns={medicationColumns} data={scriptData?.drugs || []} />
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
                <Button 
                  variant="secondary"
                  type="button"
                  onClick={handleViewInvoice}
                  style={{ marginRight: '12px' }}
                >
                  View Invoice
                </Button>
                <Button 
                  variant="primary"
                  onClick={handleProcessScript}
                  type="button"
                >
                  Process Script
                </Button>
              </div>
            </div>
          </div>
        </main>

        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
    </div>
  );
}
